import { ApiError, getGetMeQueryKey, getMe, type Me } from '@postifoto/api-client';
import type { Permission } from '@postifoto/contracts/client';
import type { QueryClient } from '@tanstack/react-query';

/** Текущий сотрудник; `null` — не вошёл (401). Кэш React Query — один источник для всех экранов. */
export async function loadMe(queryClient: QueryClient): Promise<Me | null> {
  try {
    return await queryClient.fetchQuery({
      queryKey: getGetMeQueryKey(),
      queryFn: () => getMe(),
      staleTime: 30_000,
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function refreshMe(queryClient: QueryClient): Promise<Me | null> {
  await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
  return loadMe(queryClient);
}

/**
 * Право на точке (или хотя бы на одной). Только для показа элементов интерфейса —
 * решение всегда принимает API (ADR-036).
 */
export function can(me: Me | null | undefined, permission: Permission, pointId?: string): boolean {
  const grant = me?.permissions.find((item) => item.permission === permission);
  if (!grant) return false;
  if (grant.scope === 'all' || pointId === undefined) return true;
  return grant.scope.includes(pointId);
}

export function canGlobal(me: Me | null | undefined, permission: Permission): boolean {
  return me?.permissions.find((item) => item.permission === permission)?.scope === 'all';
}

/** Точки, где у сотрудника есть хотя бы одна роль; `'all'` — роль на все точки. */
export function myPoints(me: Me | null | undefined): 'all' | Set<string> {
  const points = new Set<string>();
  for (const grant of me?.permissions ?? []) {
    if (grant.scope === 'all') return 'all';
    for (const id of grant.scope) points.add(id);
  }
  return points;
}
